import * as AppleAuthentication from 'expo-apple-authentication';
import * as React from 'react';
import { Image, StyleSheet } from 'react-native';

import { useAppleAuthRequest, useFacebookAuthRequest, useGoogleAuthRequest } from '@auth/Federated';
import { BigButton } from '@components/Button';
import Icon from '@components/Icon';
import { useAppTheme } from '@context/ThemeContext';
import { logError } from '@helpers/Loggers';

type FederatedSignInIconProps = {
  size: number;
};

export function GoogleSignInButton() {
  const { prompt, disabled } = useGoogleAuthRequest();

  const icon = ({ size }: FederatedSignInIconProps) => (
    <Image
      source={require('../assets/images/google-logo.png')}
      style={{ width: size, height: size }}
    />
  );

  return (
    <BaseFederatedSignInButton
      displayName="Google"
      icon={icon}
      signInPrompt={prompt}
      disabled={disabled}
    />
  );
}

export function AppleSignInButton() {
  const { prompt, disabled } = useAppleAuthRequest();
  const [isAvailable, setIsAvailable] = React.useState(false);
  const appTheme = useAppTheme();

  React.useEffect(() => {
    AppleAuthentication.isAvailableAsync()
      .then(setIsAvailable)
      .catch((error) => logError(error));
  }, []);

  const icon = ({ size }: FederatedSignInIconProps) => (
    <Icon name="apple" size={size} color={appTheme.dark ? 'white' : 'black'} />
  );

  return (
    <>
      {isAvailable && (
        <BaseFederatedSignInButton
          displayName="Apple"
          icon={icon}
          signInPrompt={prompt}
          disabled={disabled}
        />
      )}
    </>
  );
}

export function FacebookSignInButton() {
  const { prompt, disabled } = useFacebookAuthRequest();

  const icon = ({ size }: FederatedSignInIconProps) => (
    <Image
      source={require('../assets/images/facebook-logo.png')}
      style={{ width: size, height: size }}
    />
  );

  return (
    <BaseFederatedSignInButton
      displayName="Facebook"
      icon={icon}
      signInPrompt={prompt}
      disabled={disabled}
    />
  );
}

type BaseFederatedSignInButtonProps = {
  displayName: string;
  icon: ({ size }: FederatedSignInIconProps) => React.ReactNode;
  signInPrompt: () => Promise<unknown>;
  disabled?: boolean;
};

export function BaseFederatedSignInButton({
  displayName,
  icon,
  signInPrompt,
  disabled,
}: BaseFederatedSignInButtonProps) {
  const onPress = async () => {
    try {
      await signInPrompt();
    } catch (error) {
      logError(error);
    }
  };

  const appTheme = useAppTheme();

  return (
    <BigButton
      icon={icon}
      onPress={onPress}
      mode="outlined"
      textColor={appTheme.dark ? 'white' : 'black'}
      style={styles.buttonStyle}
      labelStyle={styles.buttonLabelStyle}
      uppercase={false}
      disabled={disabled}>
      Continue with {displayName}
    </BigButton>
  );
}

const styles = StyleSheet.create({
  buttonLabelStyle: {
    fontWeight: 'normal',
  },
  buttonStyle: {
    borderWidth: 1.5,
  },
});
